import React from "react";

export function renderNetraaAPMReleaseNotes2() {
  return <RenderNetraaAPMReleaseNotes />;
}

function RenderNetraaAPMReleaseNotes() {
  const releaseMetrics = [
    { label: "Modules Updated", value: "09" },
    { label: "New Capabilities", value: "09" },
    { label: "Security & Governance", value: "1" },
    { label: "Impact Score", value: "9.2" },
  ];

  const releasePills = [
    "GlassFish & Jetty 12 agent support",
    "HTTP response status monitoring",
    "License & client management",
    "Control Zone module",
  ];

  const majorFeatureEnhancements = [
    {
      number: 1,
      title: "Java Agent & Server Support",
      subsections: [
        {
          title: "GlassFish Server Support",
          items: [
            "Added monitoring support for Java applications deployed on GlassFish Server.",
            "Enables automatic instrumentation with end-to-end observability — no changes required to application code.",
            "Provides full request tracing, performance metrics, and health visibility for GlassFish-hosted services.",
          ],
        },
        {
          title: "Jetty 12 Plugin Support",
          items: [
            "Developed a new Netraa APM agent plugin for Jetty 12.",
            "Captures incoming HTTP requests and provides detailed request tracing and traffic visibility.",
            "Improves observability for applications hosted on Jetty 12 without requiring manual instrumentation.",
          ],
        },
      ],
    },
    {
      number: 2,
      title: "Server & Application Monitoring",
      subsections: [
        {
          title: "Technology-Based Application Categorization",
          items: [
            "Applications are now categorized by their technology type with matching technology-specific icons.",
            "Makes it quicker to identify and navigate to monitored applications at a glance.",
          ],
        },
        {
          title: "HTTP Response Status Monitoring",
          items: [
            "Added monitoring for HTTP response status codes across all ranges: 1xx, 2xx, 3xx, 4xx, and 5xx.",
            "Provides clear, categorized visibility into service health — whether requests are succeeding, being redirected, or failing.",
            "Simplifies troubleshooting by making it easy to spot error trends across monitored services.",
          ],
        },
        {
          title: "Trace Filtering by Tags",
          items: [
            "Added the ability to filter traces by Tags, allowing teams to quickly narrow down and find specific transactions or requests.",
            "Reduces time spent searching through traces and improves the speed of investigation.",
          ],
        },
      ],
    },
    {
      number: 3,
      title: "License & Client Management",
      subsections: [
        {
          title: "License Overview & Usage Tracking",
          items: [
            "Enhanced the Client License Overview to display real-time visibility into license unit consumption — showing how many units are available, how many are currently in use, and the remaining capacity at a glance.",
            "Improved the License Application Mapping page with live license usage tracking, making it easy to understand exactly how licenses are distributed and consumed across individual applications and hosts.",
          ],
        },
        {
          title: "Client Creation & License Document",
          items: [
            "Added company contact details to the client creation workflow — up to 3 contacts can now be added per client, ensuring accurate contact information is captured at the point of client setup.",
            "Updated the license document template to include the new client contact information, ensuring all generated license documents reflect the latest client details.",
          ],
        },
      ],
    },
    {
      number: 4,
      title: "Infrastructure & Platform Monitoring",
      subsections: [
        {
          title: "Infrastructure Dashboard Improvements",
          items: [
            "Improved monitoring dashboards for Amazon EKS, Amazon RDS, and IIS environments with better visibility into infrastructure health.",
            "Enhanced the Overview page to provide a clearer consolidated view across all monitored infrastructure.",
          ],
        },
        {
          title: "Control Zone Management",
          items: [
            "Introduced the new Control Zone module, providing centralized management of monitoring zones across the platform.",
            "Includes enhanced filtering and improved access control to ensure teams can manage their zones efficiently and securely.",
          ],
        },
        {
          title: "API Gateway & BRUM Enhancements",
          items: [
            "Enhanced the API Gateway module with user interface improvements, additional features, and improved validation.",
            "Improved the Browser Real User Monitoring (BRUM) module with additional capabilities and usability enhancements.",
          ],
        },
      ],
    },
    {
      number: 5,
      title: "Custom Dashboards",
      subsections: [
        {
          title: "Redesigned Dashboard Experience",
          items: [
            "Redesigned the dashboard with a fresh, modern look for a more intuitive and visually appealing experience.",
            "Dashboards now automatically display the right metrics based on the type of application being monitored, reducing the need for manual configuration.",
            "Added support for embedding images and arrows directly on dashboards, making it easier to create annotated, visual layouts.",
            "Users can now edit dashboard components directly from the dashboard view without needing to navigate away.",
          ],
        },
      ],
    },
  ];

  const notes = [
    "Java Agent & Server Support: added GlassFish Server monitoring and a new Jetty 12 plugin, both with automatic instrumentation and no application code changes required.",
    "Server & Application Monitoring: introduced technology-based application categorization, HTTP response status monitoring across all status ranges, and trace filtering by Tags.",
    "License & Client Management: real-time license usage tracking, live license-to-application mapping, and expanded client contact capture reflected in license documents.",
    "Infrastructure & Platform Monitoring: improved Amazon EKS, Amazon RDS, and IIS dashboards, a new Control Zone module, and enhancements to API Gateway and BRUM.",
    "Custom Dashboards: a redesigned, more intuitive dashboard experience with automatic metric selection, embeddable images and arrows, and inline component editing.",
  ];

  return (
    <div>
      <main className="transition-opacity duration-300">
        <div className="mx-auto">
          <section className="relative scroll-mt-24" id="release-details">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                Release Notes 2026.06
              </div>

              <div className="flex items-center justify-between gap-4">
                <h1 className="text-3xl font-black tracking-tight sm:text-4xl">
                  <span className="bg-gradient-to-r from-orange-500 via-red-500 to-rose-600 bg-clip-text text-transparent">
                    Netraa APM Release Notes
                  </span>
                </h1>
              </div>

              <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg dark:text-slate-300">
                Netraa APM – Release Notes
              </p>
            </div>
          </section>

          <section className="scroll-mt-24 px-0 py-4" id="intro">
            <div className="rounded-3xl border border-orange-200/70 bg-gradient-to-br from-orange-50 via-white to-white p-6 shadow-sm dark:border-orange-500/15 dark:from-orange-500/10 dark:via-slate-950/30 dark:to-slate-950/30">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25">
                  ✦
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                    Introduction
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    What this release brings to the platform.
                  </p>
                </div>
              </div>

              <p className="text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
                This release strengthens Netraa APM across nine major areas,
                spanning Java agent and server support, server and application
                monitoring, license and client management, infrastructure and
                platform monitoring, and custom dashboards. Highlights include
                new GlassFish Server and Jetty 12 agent support, HTTP response
                status monitoring, trace filtering by Tags, real-time license
                usage tracking, a new Control Zone module, and a redesigned
                custom dashboard experience. 
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {releasePills.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-10 w-full scroll-mt-24" id="analytics">
            <div className="mx-auto">
              <h4 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
                Release Analytics
              </h4>

              <div
                className="
      glass-card
      rounded-2xl
      border border-slate-200 dark:border-white/10
      bg-white/90 p-5 backdrop-blur-xl
      transition-all duration-500
      hover:-translate-y-1 hover:shadow-2xl
      dark:bg-white/5
      sm:p-6
    "
              >
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {releaseMetrics.map((item, i) => (
                    <div
                      key={i}
                      className="
              rounded-xl
              border border-slate-200 bg-white p-3
              shadow-sm
              transition-all duration-300
              hover:-translate-y-1 hover:scale-[1.02] hover:shadow-md
              dark:border-white/10 dark:bg-white/5
            "
                    >
                      <div className="mb-1 text-[11px] text-slate-500 dark:text-slate-400">
                        {item.label}
                      </div>

                      <div
                        className="
              text-lg font-bold text-orange-600
              dark:bg-gradient-to-r dark:from-orange-500 dark:to-rose-600
              dark:bg-clip-text dark:text-transparent
            "
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="mb-1 flex justify-between text-sm text-slate-700 dark:text-slate-300">
                      <span>Deployment Readiness</span>
                      <span className="font-semibold text-orange-500">98%</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="
              h-full w-[98%]
              bg-gradient-to-r from-orange-500 to-rose-600
              shadow-[0_0_8px_rgba(255,120,120,0.4)]
              transition-all duration-700
            "
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 flex justify-between text-sm text-slate-700 dark:text-slate-300">
                      <span>Health Meter</span>
                      <span className="font-semibold text-rose-500">Optimal</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="
              h-full w-full
              bg-gradient-to-r from-orange-500 to-rose-600
              shadow-[0_0_8px_rgba(255,120,120,0.4)]
              transition-all duration-700
            "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12 mt-12" id="overview">
            <h2 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
              Release Details
            </h2>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-slate-50 dark:bg-white/5">
                  <tr>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Release Month
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Organization
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Prepared By
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  <tr className="bg-white dark:bg-transparent">
                    <td className="px-5 py-4 font-medium text-orange-600 dark:text-orange-400 border border-slate-200 dark:border-white/10">
                      June 2026
                    </td>
                    <td className="px-5 py-4 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10">
                      ApMoSys Products Pvt. Ltd.
                    </td>
                    <td className="px-5 py-4j 2 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10">
                      Netraa APM Product Team
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="scroll-mt-24" id="enhancements">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Major Feature Enhancements
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Exact feature list from the release notes.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {majorFeatureEnhancements.map((section) => (
                <article
                  key={section.title}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/60"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-sm font-bold text-white shadow-md shadow-orange-500/20">
                      {section.number}
                    </div>

                    <div className="min-w-0 w-full">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        {section.title}
                      </h3>

                      <div className="mt-3 space-y-4">
                        {section.subsections.map((sub) => (
                          <div key={sub.title}>
                            <h4 className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                              {sub.title}
                            </h4>
                            <ul className="mt-2 space-y-2">
                              {sub.items.map((point) => (
                                <li
                                  key={point}
                                  className="flex gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
                                >
                                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="py-8" id="notes">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                Additional Notes
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Extra content kept in the layout.
              </p>

              <ul className="mt-6 space-y-4">
                {notes.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500" />
                    <span className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

              <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 p-6 text-white shadow-lg">
                <h3 className="text-xl font-bold">Release Summary</h3>

                <p className="mt-3 text-sm leading-6 text-white/90">
                  This release strengthens Netraa APM across nine major areas,
                  delivering new agent support, monitoring capabilities,
                  license management improvements, infrastructure visibility,
                  and a redesigned custom dashboard experience.
                </p>

                <div className="mt-5 text-sm font-semibold">
                  Status: Major enhancements and platform improvements
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}